/**
 * Editor de foto de perfil / story.
 *
 * Compõe, num único canvas, a foto enviada pelo visitante (com pan e zoom)
 * e a moldura PNG transparente escolhida. Tudo acontece no navegador —
 * nenhuma imagem é enviada ao servidor.
 */

type Moldura = { nome: string; src: string }

type ModoConfig = {
  titulo: string
  largura: number
  altura: number
  redondo: boolean
  arquivo: string
  molduras: Moldura[]
}

type Config = Record<string, ModoConfig>

const MAX_UPLOAD = 12 * 1024 * 1024 // 12 MB

function carregarImagem(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Falha ao carregar ${src}`))
    img.src = src
  })
}

function initFrameEditor(): void {
  const raiz = document.querySelector<HTMLElement>('[data-frame-editor]')
  const dados = document.getElementById('vd-frame-config')
  if (!raiz || !dados?.textContent) return

  let config: Config
  try {
    config = JSON.parse(dados.textContent) as Config
  } catch {
    return
  }

  const box = raiz.querySelector<HTMLElement>('.vd-modal__box')
  const canvas = raiz.querySelector<HTMLCanvasElement>('[data-frame-canvas]')
  const stage = raiz.querySelector<HTMLElement>('[data-frame-stage]')
  const vazio = raiz.querySelector<HTMLElement>('[data-frame-empty]')
  const dica = raiz.querySelector<HTMLElement>('[data-frame-dica]')
  const zoomWrap = raiz.querySelector<HTMLElement>('[data-frame-zoom-wrap]')
  const zoom = raiz.querySelector<HTMLInputElement>('[data-frame-zoom]')
  const input = raiz.querySelector<HTMLInputElement>('[data-frame-input]')
  const uploadLabel = raiz.querySelector<HTMLElement>(
    '[data-frame-upload-label]',
  )
  const lista = raiz.querySelector<HTMLElement>('[data-frame-list]')
  const baixar = raiz.querySelector<HTMLButtonElement>('[data-frame-download]')
  const msg = raiz.querySelector<HTMLElement>('[data-frame-msg]')
  const titulo = raiz.querySelector<HTMLElement>('[data-frame-titulo]')

  if (!canvas || !stage || !lista || !baixar || !input || !zoom) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  /* ── estado ──────────────────────────────────────────────── */
  let modo: ModoConfig | null = null
  let foto: HTMLImageElement | null = null
  let moldura: HTMLImageElement | null = null
  let escalaBase = 1
  let escala = 1
  let deslocX = 0
  let deslocY = 0
  let ultimoFoco: HTMLElement | null = null

  const aviso = (texto: string, erro = false): void => {
    if (!msg) return
    msg.textContent = texto
    msg.dataset.erro = String(erro)
  }

  /* ── desenho ─────────────────────────────────────────────── */

  const limitar = (): void => {
    if (!foto || !modo) return
    const largura = foto.width * escalaBase * escala
    const altura = foto.height * escalaBase * escala
    const limiteX = Math.max(0, (largura - modo.largura) / 2)
    const limiteY = Math.max(0, (altura - modo.altura) / 2)
    deslocX = Math.min(limiteX, Math.max(-limiteX, deslocX))
    deslocY = Math.min(limiteY, Math.max(-limiteY, deslocY))
  }

  const desenhar = (): void => {
    if (!modo) return

    ctx.clearRect(0, 0, modo.largura, modo.altura)

    if (foto) {
      const largura = foto.width * escalaBase * escala
      const altura = foto.height * escalaBase * escala

      ctx.save()

      // No modo perfil a foto é recortada em círculo: as quinas ficam
      // transparentes e a arte final já sai redonda.
      if (modo.redondo) {
        ctx.beginPath()
        ctx.arc(
          modo.largura / 2,
          modo.altura / 2,
          Math.min(modo.largura, modo.altura) / 2,
          0,
          Math.PI * 2,
        )
        ctx.clip()
      }

      ctx.drawImage(
        foto,
        modo.largura / 2 + deslocX - largura / 2,
        modo.altura / 2 + deslocY - altura / 2,
        largura,
        altura,
      )

      ctx.restore()
    }

    if (moldura) {
      ctx.drawImage(moldura, 0, 0, modo.largura, modo.altura)
    }
  }

  const ajustarFoto = (img: HTMLImageElement): void => {
    if (!modo) return
    foto = img
    escalaBase = Math.max(modo.largura / img.width, modo.altura / img.height)
    escala = 1
    deslocX = 0
    deslocY = 0
    zoom.value = '100'
    canvas.hidden = false
    if (vazio) vazio.hidden = true
    if (dica) dica.hidden = false
    if (zoomWrap) zoomWrap.hidden = false
    baixar.disabled = false
    desenhar()
  }

  /* ── molduras ────────────────────────────────────────────── */

  const selecionarMoldura = async (indice: number): Promise<void> => {
    if (!modo) return

    const botoes = lista.querySelectorAll('button')
    for (const [i, botao] of botoes.entries()) {
      botao.setAttribute('aria-pressed', String(i === indice))
    }

    try {
      moldura = await carregarImagem(modo.molduras[indice].src)
      desenhar()
    } catch {
      aviso('Não foi possível carregar esta moldura.', true)
    }
  }

  const montarMolduras = (): void => {
    if (!modo) return

    lista.innerHTML = modo.molduras
      .map(
        (m, i) => `
        <li>
          <button type="button" aria-pressed="${i === 0}" data-indice="${i}" title="${m.nome}">
            <img src="${m.src}" alt="${m.nome}" loading="lazy" />
          </button>
        </li>`,
      )
      .join('')

    for (const botao of lista.querySelectorAll<HTMLButtonElement>('button')) {
      botao.addEventListener('click', () => {
        void selecionarMoldura(Number(botao.dataset.indice))
      })
    }
  }

  /* ── interação: arrastar e zoom ───────────────────────────── */

  let arrastando = false
  let ultimoX = 0
  let ultimoY = 0

  const proporcaoTela = (): number => {
    if (!modo) return 1
    const rect = canvas.getBoundingClientRect()
    return rect.width > 0 ? modo.largura / rect.width : 1
  }

  canvas.addEventListener('pointerdown', (e) => {
    if (!foto) return
    arrastando = true
    ultimoX = e.clientX
    ultimoY = e.clientY
    canvas.setPointerCapture(e.pointerId)
  })

  canvas.addEventListener('pointermove', (e) => {
    if (!arrastando || !foto) return
    const fator = proporcaoTela()
    deslocX += (e.clientX - ultimoX) * fator
    deslocY += (e.clientY - ultimoY) * fator
    ultimoX = e.clientX
    ultimoY = e.clientY
    limitar()
    desenhar()
  })

  const soltar = (e: PointerEvent): void => {
    if (!arrastando) return
    arrastando = false
    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId)
    }
  }
  canvas.addEventListener('pointerup', soltar)
  canvas.addEventListener('pointercancel', soltar)

  canvas.addEventListener(
    'wheel',
    (e) => {
      if (!foto) return
      e.preventDefault()
      const atual = Number(zoom.value)
      const proximo = Math.min(
        400,
        Math.max(100, atual - Math.sign(e.deltaY) * 8),
      )
      zoom.value = String(proximo)
      escala = proximo / 100
      limitar()
      desenhar()
    },
    { passive: false },
  )

  zoom.addEventListener('input', () => {
    escala = Number(zoom.value) / 100
    limitar()
    desenhar()
  })

  /* ── upload ──────────────────────────────────────────────── */

  input.addEventListener('change', () => {
    const arquivo = input.files?.[0]
    if (!arquivo) return

    if (!arquivo.type.startsWith('image/')) {
      aviso('Escolha um arquivo de imagem (JPG ou PNG).', true)
      return
    }

    if (arquivo.size > MAX_UPLOAD) {
      aviso('Imagem muito grande. Envie um arquivo de até 12 MB.', true)
      return
    }

    const url = URL.createObjectURL(arquivo)
    carregarImagem(url)
      .then((img) => {
        ajustarFoto(img)
        if (uploadLabel) uploadLabel.textContent = 'Trocar foto'
        aviso('')
      })
      .catch(() => aviso('Não foi possível abrir esta imagem.', true))
      .finally(() => URL.revokeObjectURL(url))
  })

  /* ── download ────────────────────────────────────────────── */

  baixar.addEventListener('click', () => {
    if (!foto || !modo) return
    const nomeArquivo = modo.arquivo

    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          aviso('Não foi possível gerar a imagem. Tente novamente.', true)
          return
        }
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = nomeArquivo
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        aviso('Imagem baixada! Agora é só publicar.')
      }, 'image/png')
    } catch {
      aviso('Seu navegador bloqueou o download. Tente outro navegador.', true)
    }
  })

  /* ── abrir / fechar ──────────────────────────────────────── */

  const abrir = async (chave: string): Promise<void> => {
    const alvo = config[chave]
    if (!alvo || alvo.molduras.length === 0) return

    modo = alvo
    canvas.width = alvo.largura
    canvas.height = alvo.altura
    stage.style.setProperty('--vd-ratio', `${alvo.largura} / ${alvo.altura}`)
    lista.style.setProperty('--vd-ratio', `${alvo.largura} / ${alvo.altura}`)
    if (titulo) titulo.textContent = alvo.titulo

    foto = null
    moldura = null
    canvas.hidden = true
    if (vazio) vazio.hidden = false
    if (dica) dica.hidden = true
    if (zoomWrap) zoomWrap.hidden = true
    baixar.disabled = true
    input.value = ''
    if (uploadLabel) uploadLabel.textContent = 'Escolher foto'
    aviso('')

    montarMolduras()

    ultimoFoco = document.activeElement as HTMLElement
    raiz.hidden = false
    // força reflow para a transição de entrada rodar
    void raiz.offsetHeight
    raiz.dataset.state = 'open'
    document.body.style.overflow = 'hidden'
    box?.focus()

    await selecionarMoldura(0)
  }

  const fechar = (): void => {
    raiz.dataset.state = 'closed'
    document.body.style.overflow = ''
    window.setTimeout(() => {
      raiz.hidden = true
    }, 200)
    ultimoFoco?.focus()
  }

  for (const botao of document.querySelectorAll<HTMLElement>(
    '[data-frame-open]',
  )) {
    botao.addEventListener('click', () => {
      void abrir(botao.dataset.frameOpen ?? '')
    })
  }

  for (const botao of raiz.querySelectorAll<HTMLElement>(
    '[data-frame-close]',
  )) {
    botao.addEventListener('click', fechar)
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && raiz.dataset.state === 'open') fechar()
  })
}

document.addEventListener('DOMContentLoaded', initFrameEditor)
